import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadSingleFile } from "../../services/FileDownloader";
import { useCarouselContext } from "../../context/CarouselContext";

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 5;

const buttonStyle = {
  fontSize: 18,
  padding: "6px 12px",
  borderRadius: "8px",
  backgroundColor: "rgba(200, 200, 200, 0.25)",
  color: "white",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const DraggableImageContainer: React.FC = () => {
  const { selectedImage } = useCarouselContext();
  const src = selectedImage?.src || "";
  const alt = `Zoomed view of ${selectedImage?.tag || "image"}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const [fitZoom, setFitZoom] = useState(1);   // computed "fits in view"
  const [zoom, setZoom] = useState(1);         // current zoom

  const computeFitZoom = useCallback(() => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    // If the image isn't loaded yet, natural sizes will be 0
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (!iw || !ih) return;

    const cw = container.clientWidth;
    const ch = container.clientHeight;

    // Fit INSIDE the container (no cropping). Add a bit of padding.
    const scale = Math.min(cw / iw, ch / ih) * 0.95;

    const clamped = Math.min(Math.max(scale, MIN_ZOOM), MAX_ZOOM);
    setFitZoom(clamped);
    setZoom(clamped);
    setPosition({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    const handleMouseUpGlobal = () => setDragging(false);
    window.addEventListener("mouseup", handleMouseUpGlobal);
    return () => window.removeEventListener("mouseup", handleMouseUpGlobal);
  }, []);

  // Recompute on src change (image load will trigger computeFitZoom via onLoad too)
  useEffect(() => {
    // Reset position immediately so old pan doesn't flash for a frame
    setPosition({ x: 0, y: 0 });
  }, [src]);

  // Recompute when container resizes (fullscreen toggle, window resize, etc.)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(() => computeFitZoom());
    ro.observe(el);
    return () => ro.disconnect();
  }, [computeFitZoom]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setDragging(true);
    setStartPos({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragging) return;
    setPosition({ x: e.clientX - startPos.x, y: e.clientY - startPos.y });
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;

    // Let users zoom out to (some fraction of) fitZoom instead of a magic 0.25
    const min = Math.max(fitZoom * 0.5, MIN_ZOOM);
    setZoom((prev) => Math.min(Math.max(prev + delta, min), MAX_ZOOM));
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, MAX_ZOOM));
  const handleZoomOut = () => {
    const min = Math.max(fitZoom * 0.5, MIN_ZOOM);
    setZoom((prev) => Math.max(prev - 0.1, min));
  };

  const resetView = () => {
    setZoom(fitZoom);
    setPosition({ x: 0, y: 0 });
  };

  return (
    <Box
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onWheel={handleWheel}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        cursor: dragging ? "grabbing" : "grab"
      }}
    >
      <Box
        sx={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
          transition: dragging ? "none" : "transform 0.2s ease-out",
          maxWidth: "none",
          maxHeight: "none",
          userSelect: "none",
          pointerEvents: "none"
        }}
      >
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          onLoad={computeFitZoom}  // <-- critical
          style={{
            display: "block",
            maxWidth: "none",
            maxHeight: "none",
            userSelect: "none",
            pointerEvents: "none",
          }}
          draggable={false}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          display: "flex",
          justifyContent: "center",
          width: "100%",
          gap: 1,
          zIndex: 1,
        }}
      >
        <button onClick={handleZoomOut} style={buttonStyle}>-</button>
        <button onClick={() => downloadSingleFile(src)} style={buttonStyle}><DownloadIcon /></button>
        <button onClick={resetView} style={buttonStyle}><RestartAltIcon /></button>
        <button onClick={handleZoomIn} style={buttonStyle}>+</button>
      </Box>
    </Box>
  );
};

export default DraggableImageContainer;
